/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { API_BASE } from '../config';
import { invalidateCache } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check auth status on mount (cookie is sent automatically)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_BASE}/auth/me`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
        } else {
          // Not authenticated
          setUser(null);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setUser(null);
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = useCallback(async (username, password) => {
    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (response.ok && data.user) {
        setUser(data.user);
        invalidateCache('');
        return { success: true, user: data.user };
      } else {
        return { 
          success: false, 
          error: data.message || 'Inloggning misslyckades' 
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: 'Kunde inte ansluta till servern' 
      };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Call backend to clear the cookie
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    invalidateCache('');
  }, []);

  const hasRole = useCallback((requiredRole) => {
    if (!user) return false;
    const roleHierarchy = { 'readOnly': 1, 'contentCreator': 2, 'sekreterare': 3, 'admin': 4 };
    const userLevel = roleHierarchy[user.role] || 0;
    const requiredLevel = roleHierarchy[requiredRole] || 1;
    return userLevel >= requiredLevel;
  }, [user]);

  const apiFetch = useCallback(async (endpoint, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
        credentials: 'include'
      });

      if (response.status === 401) {
        // Token expired or invalid
        logout();
        return { error: 'Unauthorized', status: 401 };
      }

      const data = await response.json();
      return { data, status: response.status, ok: response.ok };
    } catch (error) {
      console.error('API fetch error:', error);
      return { error: 'Network error', status: 0, ok: false };
    }
  }, [logout]);

  const value = {
    user,
    loading,
    login,
    logout,
    hasRole,
    apiFetch,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
